import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";

const programmes = [
  {
    title: "Economic, Business & Financial Development",
    domain: "People",
    image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=800&q=80",
    description: "Strategic economic, business, and financial development advice to iwi, Maori enterprises, and community organisations. From investment strategy to business planning and financial modelling, grounded in decades of experience leading regional economies and championing Indigenous prosperity.",
    accent: "#bb321a",
  },
  {
    title: "Maori Governance & Leadership",
    domain: "People",
    image: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=800&q=80",
    description: "Governance training, board effectiveness, and leadership development grounded in tikanga and contemporary best practice. Our directors have sat across governance tables in health, education, economic development, and Maori land trusts. We bring that depth to every engagement.",
    accent: "#bb321a",
  },
  {
    title: "Maori Land Trust Development",
    domain: "Place",
    image: "https://images.unsplash.com/photo-1594028355881-9942abbaa2af?auto=format&fit=crop&w=800&q=80",
    description: "Working alongside Maori Land Trusts to identify opportunities for sustainable, intergenerational growth. From strategic planning to feasibility studies and investment pathways, helping whenua-based entities unlock the potential of their land in ways that honour the aspirations of their owners.",
    accent: "#364225",
  },
  {
    title: "MANA Marae: Climate Adaptation Platform",
    domain: "Planet",
    image: "https://images.unsplash.com/photo-1675604876442-7132e9f541a4?auto=format&fit=crop&w=800&q=80",
    description: "A digital platform we built that empowers marae to develop their own Climate Adaptation Plans. Communities assess risk, plan for resilience, and contribute evidence that informs policy at local, regional, and national levels. Indigenous-led climate action, from the ground up.",
    accent: "#45443f",
  },
  {
    title: "Digital Procurement & Infrastructure Intelligence",
    domain: "Place",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80",
    description: "Our procurement and measurement tool tracks, manages, and determines the value of large national infrastructure projects. It shows how much investment stays in the regions, how much leaks out, and how much reaches Maori and Pasifika communities. It also manages contracts and contractors, prequalifying and tiering them for readiness.",
    accent: "#364225",
  },
  {
    title: "Papakainga Housing: Eastern Bay of Plenty",
    domain: "Place",
    image: "https://images.unsplash.com/photo-1575715292144-a8c53a8b635a?auto=format&fit=crop&w=800&q=80",
    description: "Leading a housing development programme and a 30-home papakainga build in isolated rural communities in the eastern Bay of Plenty. Long-term housing stability for whanau who have been underserved for generations, designed in partnership, built with purpose, and owned by the community.",
    accent: "#364225",
  },
  {
    title: "Research: Te Whakapapa o te Kaiawhina",
    domain: "People",
    image: "https://images.unsplash.com/photo-1635185481339-ec107276a2a4?auto=format&fit=crop&w=800&q=80",
    description: "Engaged by the New Zealand Nursing Organisation to identify the Whakapapa of Kaiawhina in Aotearoa. This multi-phase research project traces the origins, evolution, and contemporary significance of the kaiawhina role across the health and social sectors. To date, we have completed a comprehensive Literature Review and an Insights Report. The next phase involves face-to-face interviews, wananga, and small group interviews with kaiawhina, practitioners, and sector leaders. The project will culminate in a final report that brings together all phases of the research into a single authoritative body of evidence.",
    accent: "#bb321a",
  },
];

const domainGradient = {
  People: "from-[#364225]/90",
  Place: "from-[#364225]/90",
  Planet: "from-[#364225]/90",
};

export default function OurMahiSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [activeMahi, setActiveMahi] = useState(null);

  return (
    <section
      id="mahi"
      data-testid="our-mahi-section"
      className="relative py-16 sm:py-24 overflow-hidden"
      style={{ backgroundColor: "#FDFBF7" }}
    >
      <div ref={ref} className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-5"
        >
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#bb321a] mb-4">Our Mahi</p>
          <h2 className="font-['Syne'] text-4xl sm:text-5xl font-bold tracking-tight text-[#bb321a] leading-[1.05]">
            Impact in <span className="text-[#bb321a]">action.</span>
          </h2>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="text-base sm:text-lg text-[#2D2A26] leading-relaxed max-w-4xl mb-14"
        >
          T.I.I.N.A. was built by qualified practitioners, not theorists. Every programme we lead is grounded in decades of collective experience and professional expertise across governance, economic development, research, engineering, and community development. We do not consult from the sidelines. We build platforms, lead programmes, and deliver outcomes that shift systems and create lasting value for Indigenous communities.
        </motion.p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {programmes.map((prog, i) => {
            const isOpen = activeMahi === i;
            const grad = domainGradient[prog.domain];
            return (
              <motion.div
                key={prog.title}
                data-testid={`mahi-card-${i}`}
                initial={{ opacity: 0, y: 25 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.08 * i }}
                onClick={() => setActiveMahi(isOpen ? null : i)}
                className="relative group cursor-pointer overflow-hidden"
              >
                <div className="relative h-[280px] sm:h-[300px] overflow-hidden">
                  <img src={prog.image} alt={prog.title} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-black/60" />
                  <div className={`absolute inset-0 bg-gradient-to-t ${grad} via-transparent to-transparent opacity-60`} />
                  <div className="absolute inset-0 flex flex-col justify-end p-5 sm:p-6">
                    <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-white/50 mb-1">{prog.domain}</span>
                    <h3 className="font-['Syne'] text-base sm:text-lg font-bold text-white leading-tight mb-2">{prog.title}</h3>
                    <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isOpen ? "max-h-48 opacity-100" : "max-h-0 opacity-0"}`}>
                      <div className="bg-black/90 p-3 mt-1">
                        <p className="text-xs sm:text-sm text-white leading-relaxed">{prog.description}</p>
                      </div>
                    </div>
                    {!isOpen && <div className="h-0.5 w-8 bg-white/30 mt-2 group-hover:w-16 group-hover:bg-[#bb321a] transition-all duration-500" />}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
